import { BarChart3 } from "lucide-react";
import { useQuery, type QueryStatus } from "@tanstack/react-query";

import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { LockedContent } from "@/components/layout/locked-content";

import { cn, message, formatSnakeCase } from "@/lib/utils";
import { getScoreTextColor, getScoreBarColor } from "@/lib/chart";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";
import type { ChannelPerformanceItem } from "@/schema/brand-os/brand-intel";

function ChannelRow({ platform, average_score }: ChannelPerformanceItem) {
  const label = formatSnakeCase(platform);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        <p className={cn("text-xs font-medium", getScoreTextColor(average_score))}>{Math.round(average_score)}%</p>
      </div>
      <Progress value={average_score} className="h-2 w-full bg-muted" indicatorClassName={getScoreBarColor(average_score)} />
    </div>
  );
}

function ChannelSkeleton() {
  return Array.from({ length: 3 }, (_, i) => (
    <div key={i} className="space-y-1.5">
      <div className="flex justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-10" />
      </div>
      <Skeleton className="h-2 w-full rounded-full" />
    </div>
  ));
}

function ChannelContent({ status, items }: { status: QueryStatus; items: ChannelPerformanceItem[] }) {
  switch (status) {
    case "pending":
      return <ChannelSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return items.map((item) => <ChannelRow key={item.platform} {...item} />);
  }
}

export function ChannelPerformance() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchOverview());
  const items = data?.channel_performance ?? [];

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center gap-2">
        <div className="rounded-md bg-muted p-2">
          <BarChart3 className="size-5 text-foreground/70" strokeWidth={2.5} />
        </div>
        <CardTitle className="text-base">Channel Performance</CardTitle>
        <CardDescription className="sr-only">Average performance score across key channels based on content quality, engagement, and growth metrics.</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pt-0 space-y-5">
        <ChannelContent status={status} items={items} />
      </CardContent>
    </Card>
  );
}
